import { createFeatureSelector, createSelector } from '@ngrx/store'
import { SELECTED_ITEM_FEATURE_KEY, SelectedItemState } from './selected-item.reducer'
import * as ItemsSelectors from 'libs/gb-front/item/data-access/src/lib/+state/items.selectors'
import * as CategoriesSelectors from 'libs/gb-front/category/data-access/src/lib/+state/categories.selectors'

export const getSelectedItemState = createFeatureSelector<SelectedItemState>(SELECTED_ITEM_FEATURE_KEY)

export const getSelectedItemLoaded = createSelector(
  getSelectedItemState,
  (selectedItemState) => selectedItemState.loaded,
)

export const getSelectedItemId = createSelector(getSelectedItemState, (selectedItemState) => selectedItemState.uuid)

export const getSelectedItem = createSelector(getSelectedItemState, (selectedItemState) =>
  // return state if item has loaded data
  selectedItemState && selectedItemState.uuid ? selectedItemState : undefined,
)

export const getSelectedItemName = createSelector(getSelectedItem, (selectedItem) => selectedItem?.name)

export const getSelectedBiddingItem = createSelector(getSelectedItem, (selectedItem) => selectedItem?.bidding_item)

export const getSelectedDonationItem = createSelector(getSelectedItem, (selectedItem) => selectedItem?.donation_item)

export const getSelectedPurchaseItem = createSelector(getSelectedItem, (selectedItem) => selectedItem?.purchase_item)

export const getSelectedRaffleItem = createSelector(getSelectedItem, (selectedItem) => selectedItem?.raffle_item)

export const getSelectedItemImages = createSelector(getSelectedItem, (selectedItem) => selectedItem?.images)

export const getSelectedItemTnC = createSelector(getSelectedItem, (selectedItem) =>
  selectedItem ? selectedItem.terms_and_conditions : '',
)

// find index of current selected item in filtered items list
export const getSelectedItemIndexFromFilteredList = createSelector(
  ItemsSelectors.selectFilteredItems,
  getSelectedItemId,
  (filteredItems, selectedItemId) => filteredItems.findIndex((item) => item.uuid === selectedItemId),
)

// find index of current selected item in category items_preview
export const getSelectedItemIndexFromCategoryItemsList = (categoryId: string) =>
  createSelector(CategoriesSelectors.getCategoryById(categoryId), getSelectedItemId, (category, selectedItemId) => {
    if (!category) return -1
    return category.items_preview.findIndex((item) => item.uuid === selectedItemId)
  })

export const getNextItemId = createSelector(getSelectedItem, (selectedItem) => selectedItem?.next_id)

export const getNextFilteredItemId = createSelector(
  ItemsSelectors.selectFilteredItems,
  getSelectedItemIndexFromFilteredList,
  (filteredItems, selectedItemIndex) =>
    selectedItemIndex < filteredItems.length - 1 ? filteredItems[selectedItemIndex + 1].uuid : undefined,
)

/**
 * Filtered category items may still need to be loaded so
 * to avoid the delay in showing the next button first check
 * items_preview. If no next item in items_preview then check
 * the filtered item list.
 */
export const getNextCategoryItemId = (categoryId: string) =>
  createSelector(
    CategoriesSelectors.getCategoryById(categoryId),
    getSelectedItemIndexFromCategoryItemsList(categoryId),
    getNextFilteredItemId,
    (category, selectedItemIndex, nextFilteredItemId) => {
      if (category && selectedItemIndex >= 0 && selectedItemIndex < category.items_preview.length - 1) {
        return category.items_preview[selectedItemIndex + 1].uuid
      }
      return nextFilteredItemId
    },
  )

export const getPreviousItemId = createSelector(getSelectedItem, (selectedItem) => selectedItem?.prev_id)

export const getPreviousFilteredItemId = createSelector(
  ItemsSelectors.selectFilteredItems,
  getSelectedItemIndexFromFilteredList,
  (filteredItems, selectedItemIndex) => (selectedItemIndex > 0 ? filteredItems[selectedItemIndex - 1].uuid : undefined),
)

/**
 * Filtered category items may still need to be loaded so
 * to avoid the delay in showing the prev button first check
 * items_preview. If no prev item in items_preview then check
 * the filtered item list.
 */
export const getPreviousCategoryItemId = (categoryId: string) =>
  createSelector(
    CategoriesSelectors.getCategoryById(categoryId),
    getSelectedItemIndexFromCategoryItemsList(categoryId),
    getPreviousFilteredItemId,
    (category, selectedItemIndex, previousFilteredItemId) => {
      if (category && selectedItemIndex > 0) {
        return category.items_preview[selectedItemIndex - 1].uuid
      }
      return previousFilteredItemId
    },
  )

export const getCurrentAmount = createSelector(
  getSelectedItemState,
  (selectedItemState) => selectedItemState.amount || 0,
)
